/* Native skin scan — TEST BUILD (proof of concept, not production).
 *
 * The whole scan runs without FaceAge:
 *  - Instructions step (consent-gated Get Started), like the vendor's.
 *  - Camera capture with LIVE guidance: MediaPipe FaceMesh runs on the video
 *    feed and drives the Lighting / Look Straight / Face Position pills;
 *    Capture unlocks when all three read Good. Drag&drop / browse upload as
 *    the alternate mode, same as the vendor's two capture modes.
 *  - The vendor's EXACT scan animation (ported from the face-age@2.3.13
 *    bundle: 6s loop, y-sorted dot cascade, glowing line riding the sweep).
 *  - Scoring from the self-hosted skin-scan OSS API
 *    (github.com/DurtyDhiana/skin-scan, POST /scan -> 7 heuristic scores).
 *
 * TEST CAVEATS:
 *  - Scores are the OSS repo's 7 concerns, NOT FaceAge's 8; mapping later.
 *  - Live-check thresholds are ours (lenient), not the vendor's numbers.
 *  - Endpoint defaults to http://localhost:8000 — run the API locally and
 *    test from the `shopify theme dev` preview (http, no mixed content).
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-skin-scan-native]');
  if (!root) return;

  var ENDPOINT = root.getAttribute('data-scan-endpoint') || 'http://localhost:8000/scan';
  var MP_CDN = 'https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/';

  var CONCERNS = [
    { key: 'redness', label: 'Redness', color: 'rgb(203, 46, 130)' },
    { key: 'oiliness', label: 'Oiliness', color: 'rgb(255, 175, 20)' },
    { key: 'texture', label: 'Texture', color: 'rgb(113, 204, 81)' },
    { key: 'pores', label: 'Pores', color: 'rgb(76, 158, 255)' },
    { key: 'blemishes', label: 'Blemishes', color: 'rgb(255, 129, 227)' },
    { key: 'hydration', label: 'Hydration', color: 'rgb(0, 0, 160)' },
    { key: 'pigment', label: 'Pigment', color: 'rgb(205, 134, 242)' }
  ];
  var CIRCUMFERENCE = 126.92; /* vendor ring: r=20.2 in a 22 22 44 44 viewBox */
  var GOOD = 'rgb(27, 44, 30)', BAD = '#f04a29'; /* vendor pill colours (Annique green / their red) */

  var video = root.querySelector('[data-scan-video]');
  var photoCanvas = root.querySelector('[data-scan-photo]');
  var animCanvas = root.querySelector('[data-scan-anim]');
  var chipsBox = root.querySelector('[data-scan-chips]');
  var errBox = root.querySelector('[data-scan-error]');
  var captureBtn = root.querySelector('[data-scan-capture]');
  var consentBox = root.querySelector('[data-scan-consent]');
  var startBtn = root.querySelector('[data-scan-start]');
  var helperText = root.querySelector('[data-scan-helper]');

  var stream = null;
  var animating = false;
  var pts = null;         /* capture landmarks in canvas px, y-sorted */
  var liveFm = null;      /* FaceMesh instance for the live camera checks */
  var liveOn = false;
  var lumaCanvas = document.createElement('canvas');

  var card = root.querySelector('.scan-card');

  function show(name) {
    Array.prototype.forEach.call(root.querySelectorAll('[data-scan-view]'), function (v) {
      if (v.getAttribute('data-scan-view') === name) v.removeAttribute('hidden');
      else v.setAttribute('hidden', '');
    });
    /* Instructions render as a page-level window (like the vendor's
     * info_window); the dark 3:5 card only exists for the capture states. */
    if (card) {
      if (name === 'intro') card.setAttribute('hidden', '');
      else card.removeAttribute('hidden');
    }
  }

  function fail(msg) {
    if (errBox) { errBox.textContent = msg; errBox.removeAttribute('hidden'); }
  }
  function clearFail() { if (errBox) errBox.setAttribute('hidden', ''); }

  /* ---- instructions / consent ------------------------------------------ */

  var consented = false;
  if (consentBox) consentBox.addEventListener('click', function () {
    consented = !consented;
    consentBox.classList.toggle('active', consented);
    if (startBtn) startBtn.classList.toggle('disable', !consented);
  });
  if (startBtn) startBtn.addEventListener('click', function () {
    if (!consented) return;
    startCamera();
  });

  /* ---- camera + live guidance ------------------------------------------ */

  function setPill(key, good) {
    var pill = root.querySelector('[data-scan-pill="' + key + '"]');
    if (!pill) return;
    pill.style.backgroundColor = good ? GOOD : BAD;
    var v = pill.querySelector('.value');
    if (v) v.textContent = good ? 'Good' : 'Not Good';
  }

  function setCaptureEnabled(on) {
    if (!captureBtn) return;
    captureBtn.classList.toggle('disable', !on);
  }

  function startCamera() {
    clearFail();
    navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 720 }, height: { ideal: 1200 } } })
      .then(function (s) {
        stream = s;
        video.srcObject = s;
        video.play();
        show('camera');
        setCaptureEnabled(false);
        startLiveChecks();
      })
      .catch(function () {
        fail('Camera unavailable — upload a photo instead.');
        show('upload');
      });
  }

  function stopCamera() {
    liveOn = false;
    if (stream) { stream.getTracks().forEach(function (t) { t.stop(); }); stream = null; }
  }

  function newFaceMesh(cb) {
    var fm = new FaceMesh({ locateFile: function (f) { return MP_CDN + f; } });
    fm.setOptions({ maxNumFaces: 1, refineLandmarks: false, minDetectionConfidence: 0.5, minTrackingConfidence: 0.5 });
    fm.onResults(cb);
    return fm;
  }

  function startLiveChecks() {
    if (typeof FaceMesh === 'undefined') { setCaptureEnabled(true); return; } /* no mesh, no gate */
    if (!liveFm) liveFm = newFaceMesh(onLiveResults);
    liveOn = true;
    pump();
  }

  function pump() {
    if (!liveOn) return;
    if (!video.videoWidth) { setTimeout(pump, 100); return; }
    liveFm.send({ image: video })
      .then(function () { setTimeout(pump, 200); }) /* ~5fps is plenty for guidance */
      .catch(function () { liveOn = false; setCaptureEnabled(true); });
  }

  /* Our test versions of the vendor's analyzeLighting / getLookStraight /
   * position checks. Lenient thresholds; landmarks are normalised 0..1.
   * Key points: 1 nose tip, 10 forehead top, 152 chin, 234/454 cheeks. */
  function onLiveResults(res) {
    if (!liveOn) return;
    var lm = res.multiFaceLandmarks && res.multiFaceLandmarks[0];
    if (!lm) {
      setPill('lighting', false); setPill('straight', false); setPill('position', false);
      if (helperText) helperText.textContent = 'Keep your face inside the circle';
      setCaptureEnabled(false);
      return;
    }
    var nose = lm[1], top = lm[10], chin = lm[152], L = lm[234], R = lm[454];

    /* Position: face centred and sized inside the oval. */
    var cx = (L.x + R.x) / 2, cy = (top.y + chin.y) / 2;
    var faceH = chin.y - top.y;
    var position = Math.abs(cx - 0.5) < 0.12 && Math.abs(cy - 0.46) < 0.14 && faceH > 0.3 && faceH < 0.75;

    /* Look straight: yaw from cheek symmetry, pitch from nose along face. */
    var dl = Math.abs(nose.x - L.x), dr = Math.abs(R.x - nose.x);
    var yawOk = dl > 0 && dr > 0 && (dl / dr) > 0.6 && (dl / dr) < 1.67;
    var noseRatio = (nose.y - top.y) / (faceH || 1);
    var pitchOk = noseRatio > 0.45 && noseRatio < 0.72;
    var straight = yawOk && pitchOk;

    /* Lighting: mean luma of the face box on a small sample. */
    var lighting = false;
    try {
      var w = 64, h = 80;
      lumaCanvas.width = w; lumaCanvas.height = h;
      var c = lumaCanvas.getContext('2d', { willReadFrequently: true });
      c.drawImage(video, 0, 0, w, h);
      var x0 = Math.max(0, Math.floor(Math.min(L.x, R.x) * w)), x1 = Math.min(w, Math.ceil(Math.max(L.x, R.x) * w));
      var y0 = Math.max(0, Math.floor(top.y * h)), y1 = Math.min(h, Math.ceil(chin.y * h));
      if (x1 > x0 && y1 > y0) {
        var d = c.getImageData(x0, y0, x1 - x0, y1 - y0).data, sum = 0, n = d.length / 4;
        for (var i = 0; i < d.length; i += 4) sum += 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
        var mean = sum / n;
        lighting = mean > 70 && mean < 215;
      }
    } catch (e) { lighting = true; /* canvas blocked — do not dead-end the flow */ }

    setPill('lighting', lighting);
    setPill('straight', straight);
    setPill('position', position);
    if (helperText) {
      helperText.textContent = !position ? 'Keep your face inside the circle'
        : !straight ? 'Look straight at the camera'
          : !lighting ? 'Find better lighting — face a window'
            : 'Hold still...';
    }
    setCaptureEnabled(lighting && straight && position);
  }

  function capture() {
    if (captureBtn && captureBtn.classList.contains('disable')) return;
    var w = video.videoWidth, h = video.videoHeight;
    if (!w) return;
    photoCanvas.width = w; photoCanvas.height = h;
    var c = photoCanvas.getContext('2d');
    /* Store mirrored so the frozen frame matches the selfie the customer
     * watched (the vendor does the same). */
    c.save(); c.scale(-1, 1); c.translate(-w, 0);
    c.drawImage(video, 0, 0, w, h);
    c.restore();
    stopCamera();
    analyze();
  }

  /* ---- upload mode ------------------------------------------------------ */

  function usePhoto(file) {
    clearFail();
    if (!file || !/^image\//.test(file.type)) { fail('That file is not an image.'); return; }
    var img = new Image();
    img.onload = function () {
      photoCanvas.width = img.naturalWidth; photoCanvas.height = img.naturalHeight;
      photoCanvas.getContext('2d').drawImage(img, 0, 0);
      URL.revokeObjectURL(img.src);
      stopCamera();
      analyze();
    };
    img.onerror = function () { fail('Could not read that image.'); };
    img.src = URL.createObjectURL(file);
  }

  var dragArea = root.querySelector('[data-scan-droparea]');
  if (dragArea) {
    ['dragenter', 'dragover'].forEach(function (ev) {
      dragArea.addEventListener(ev, function (e) { e.preventDefault(); dragArea.classList.add('active'); });
    });
    ['dragleave', 'drop'].forEach(function (ev) {
      dragArea.addEventListener(ev, function (e) { e.preventDefault(); dragArea.classList.remove('active'); });
    });
    dragArea.addEventListener('drop', function (e) {
      if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]) usePhoto(e.dataTransfer.files[0]);
    });
  }

  /* ---- mesh + the vendor's animation ------------------------------------ */

  function meshStill(cb) {
    if (typeof FaceMesh === 'undefined') { cb(null); return; }
    try {
      var fm = new FaceMesh({ locateFile: function (f) { return MP_CDN + f; } });
      fm.setOptions({ staticImageMode: true, maxNumFaces: 1, refineLandmarks: false });
      fm.onResults(function (res) {
        cb(res.multiFaceLandmarks && res.multiFaceLandmarks[0] || null);
      });
      fm.send({ image: photoCanvas }).catch(function () { cb(null); });
    } catch (e) { cb(null); }
  }

  /* Ported 1-1 from the face-age bundle's animateFaceLandmarks: photo at .85
   * alpha, 6s clock, first 85% = top-to-bottom dot cascade with the glowing
   * line at the sweep's current landmark row, last 15% = fade-out together. */
  function drawLoop() {
    if (!animating) return;
    var w = animCanvas.width, h = animCanvas.height;
    var ctx = animCanvas.getContext('2d');
    ctx.clearRect(0, 0, w, h);
    ctx.save();
    ctx.globalAlpha = 0.85;
    ctx.drawImage(photoCanvas, 0, 0, w, h);
    ctx.restore();
    if (pts) {
      var f = 0.85, m = (performance.now() % 6000) / 6000;
      for (var g = 0; g < pts.length; g++) {
        var v = g / pts.length, k = 0;
        if (m < f) {
          var b = m / f;
          k = Math.max(0, Math.min(1, 10 * (b - v + 0.1)));
          k = Math.pow(k, 1.5);
        } else {
          var wRest = (m - f) / (1 - f);
          k = Math.max(0, 1 - Math.pow(wRest, 1.8));
        }
        if (k > 0) {
          ctx.fillStyle = 'rgba(255,255,255,' + k + ')';
          ctx.beginPath();
          ctx.arc(pts[g].x, pts[g].y, 1.2, 0, 2 * Math.PI);
          ctx.fill();
        }
      }
      if (m < f) {
        var A = Math.floor(pts.length * (m / f));
        var T = pts[Math.min(A, pts.length - 1)].y;
        ctx.strokeStyle = 'rgba(27, 44, 30, 0.9)'; /* Annique green, like the override's base_color */
        ctx.shadowColor = 'rgba(27, 44, 30, 0.9)';
        ctx.shadowBlur = 12;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(0, T);
        ctx.lineTo(w, T);
        ctx.stroke();
        ctx.shadowBlur = 0;
      }
    }
    requestAnimationFrame(drawLoop);
  }

  /* ---- analyze: animation + API in parallel ------------------------------ */

  var MIN_ANALYZE_MS = 4500; /* let the scan animation breathe before results */

  function analyze() {
    show('analyzing');
    animCanvas.width = photoCanvas.width;
    animCanvas.height = photoCanvas.height;
    animating = true;
    pts = null;
    drawLoop(); /* photo shows immediately; dots join when the mesh lands */
    var started = performance.now();

    meshStill(function (lm) {
      if (!lm) {
        animating = false;
        fail('No face found in that photo — try again.');
        show('intro');
        return;
      }
      var w = animCanvas.width, h = animCanvas.height;
      pts = lm.map(function (p) { return { x: p.x * w, y: p.y * h }; });
      pts.sort(function (a, b) { return a.y - b.y; });

      if (ENDPOINT === 'local') {
        /* In-browser scoring (skin-scan-scoring.js) — no backend at all.
         * Deferred a beat so the animation gets painted first. */
        setTimeout(function () {
          var scores = null;
          try { scores = window.__skinScanScores(photoCanvas, lm); } catch (e) { /* falls through */ }
          if (!scores) {
            animating = false;
            fail('Local scoring failed — see console.');
            show('intro');
            return;
          }
          var wait = Math.max(0, MIN_ANALYZE_MS - (performance.now() - started));
          setTimeout(function () { showResults(scores); }, wait);
        }, 400);
        return;
      }

      /* Remote mode: the self-hosted skin-scan OSS API. */
      photoCanvas.toBlob(function (blob) {
        var fd = new FormData();
        fd.append('image', blob, 'scan.jpg');
        fetch(ENDPOINT, { method: 'POST', body: fd })
          .then(function (r) {
            if (!r.ok) throw new Error('scan api ' + r.status);
            return r.json();
          })
          .then(function (data) { showResults(data && data.scores); })
          .catch(function () {
            animating = false;
            fail('Scan service unreachable at ' + ENDPOINT + ' — is it running?');
            show('intro');
          });
      }, 'image/jpeg', 0.92);
    });
  }

  /* ---- results ------------------------------------------------------------ */

  /* Numeric display (Sheldon's call for the native scan): 0-100, ring arc
   * matches the same number. The FaceAge widget shows words instead — that
   * convention stays over there. */
  function word(score) {
    return String(Math.round(Math.max(0, Math.min(1, score)) * 100));
  }

  function chipHtml(c, score) {
    var offset = CIRCUMFERENCE * (1 - Math.max(0, Math.min(1, score)));
    return '<div class="point_analysis" data-key="' + c.key + '">' +
      '<div class="count_box">' +
      '<div class="progress-container">' +
      '<svg class="ring" viewBox="22 22 44 44"><circle cx="44" cy="44" r="20.2" fill="none" stroke-width="2.6" stroke="' + c.color + '" stroke-dasharray="' + CIRCUMFERENCE + '" stroke-dashoffset="' + offset.toFixed(2) + '" transform="rotate(-90 44 44)"></circle></svg>' +
      '<div class="progress-label" style="color:' + c.color + '"><span>' + word(score) + '</span></div>' +
      '</div></div>' +
      '<div class="label_box">' + c.label + '</div>' +
      '</div>';
  }

  function showResults(scores) {
    animating = false;
    if (!scores) { fail('Scan service returned no scores.'); show('intro'); return; }
    var w = animCanvas.width, h = animCanvas.height;
    var ctx = animCanvas.getContext('2d');
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(photoCanvas, 0, 0, w, h);
    chipsBox.innerHTML = CONCERNS.map(function (c) {
      return chipHtml(c, typeof scores[c.key] === 'number' ? scores[c.key] : 0);
    }).join('');
    show('results');
  }

  /* ---- wire up -------------------------------------------------------------- */

  if (captureBtn) captureBtn.addEventListener('click', capture);
  Array.prototype.forEach.call(root.querySelectorAll('[data-scan-retake]'), function (b) {
    b.addEventListener('click', function () { animating = false; startCamera(); });
  });
  Array.prototype.forEach.call(root.querySelectorAll('[data-scan-to-upload]'), function (b) {
    b.addEventListener('click', function () { stopCamera(); clearFail(); show('upload'); });
  });
  Array.prototype.forEach.call(root.querySelectorAll('[data-scan-to-camera]'), function (b) {
    b.addEventListener('click', startCamera);
  });
  Array.prototype.forEach.call(root.querySelectorAll('[data-scan-to-intro]'), function (b) {
    b.addEventListener('click', function () { stopCamera(); clearFail(); show('intro'); });
  });
  Array.prototype.forEach.call(root.querySelectorAll('[data-scan-file]'), function (fi) {
    fi.addEventListener('change', function () {
      if (fi.files && fi.files[0]) usePhoto(fi.files[0]);
    });
  });
  /* Vendor DOM: "browse" is a bare span next to a hidden input. */
  Array.prototype.forEach.call(root.querySelectorAll('.buttonUpload'), function (b) {
    b.addEventListener('click', function () {
      var fi = root.querySelector('[data-scan-file]');
      if (fi) fi.click();
    });
  });

  /* ---- quiz handoff (same events as the FaceAge scan page) --------------- */
  /* When the native quiz gate shares the page, the scan hides until the quiz
   * completes; "Get my personalised routine" on our results hands off to the
   * quiz section's IN-PLACE product screens (skinquiz:results), and their
   * back button (skinquiz:backtoscan) lands right back on these results —
   * nothing reloads, exactly like the FaceAge page's loop. */
  var quizGate = document.querySelector('[data-skin-quiz-rebuild][data-quiz-handoff]');
  if (quizGate) {
    root.setAttribute('hidden', '');
    document.addEventListener('skinquiz:complete', function () {
      root.removeAttribute('hidden');
      window.scrollTo(0, 0);
    });
    document.addEventListener('skinquiz:reopen', function () {
      root.setAttribute('hidden', '');
    });
    document.addEventListener('skinquiz:backtoscan', function () {
      root.removeAttribute('hidden');
      window.scrollTo(0, 0);
    });
  }
  Array.prototype.forEach.call(root.querySelectorAll('[data-scan-routine]'), function (b) {
    b.addEventListener('click', function () {
      if (!quizGate) return;
      root.setAttribute('hidden', '');
      document.dispatchEvent(new CustomEvent('skinquiz:results'));
    });
  });
  /* Back on the instructions step returns to the quiz's last question when
   * the gate is present (mirrors the bridge's injected Back on widget step 1). */
  Array.prototype.forEach.call(root.querySelectorAll('[data-scan-back-to-quiz]'), function (b) {
    b.addEventListener('click', function () {
      if (quizGate) document.dispatchEvent(new CustomEvent('skinquiz:reopen'));
    });
  });

  show('intro');
})();
